import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { CheckCircle, Clock, CreditCard, Bank, DeviceMobile, Money, Wallet } from '@phosphor-icons/react';
import { fmtDate, fmtTime } from '@/utils/dateFmt';
import { withErs } from '@/config/legal';

const API = process.env.REACT_APP_BACKEND_URL;
const ICONS = { stripe: CreditCard, mb_way: DeviceMobile, transferencia: Bank, numerario: Money };
const fmtEur = (n) => `${Number(n).toFixed(2).replace('.', ',')} €`;

function MethodCard({ m, busy, onPaid }) {
  const Icon = ICONS[m.key] || Wallet;
  return (
    <div className="bg-white border border-[#E8E3DB] rounded-2xl p-4" data-testid={`pay-method-${m.key}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={20} weight="duotone" className="text-[#657161]" />
        <h2 className="font-medium text-[#2E3A33]">{m.label}</h2>
      </div>
      {m.instructions && <p className="text-sm text-[#2E3A33] whitespace-pre-line mb-3" data-testid={`pay-instructions-${m.key}`}>{m.instructions}</p>}
      {m.image && <img src={`${API}${m.image}`} alt={m.label} className="w-40 h-40 object-contain rounded-xl border border-[#E8E3DB] mb-3" onError={e => { e.currentTarget.style.display = 'none'; }} />}
      {m.kind === 'stripe' ? (
        <a href={m.url} className="block text-center bg-[#657161] text-white rounded-full py-2.5 text-sm font-medium" data-testid="pay-stripe-btn">Pagar com cartão</a>
      ) : (
        <button onClick={() => onPaid(m.key)} disabled={busy} className="w-full border border-[#657161] text-[#657161] rounded-full py-2.5 text-sm font-medium disabled:opacity-50" data-testid={`pay-paid-btn-${m.key}`}>Já paguei</button>
      )}
    </div>
  );
}

export default function PayPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const m = document.createElement('meta'); m.name = 'robots'; m.content = 'noindex, nofollow'; document.head.appendChild(m);
    return () => { document.head.removeChild(m); };
  }, []);
  const load = () => axios.get(`${API}/api/public/pay/${token}`).then(r => setData(r.data)).catch(e => setError(e.response?.status === 410 ? 'Este link expirou. Pede um novo link à nossa equipa.' : 'Esta ligação de pagamento não é válida.'));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const paid = async (method) => {
    setBusy(true);
    try { await axios.post(`${API}/api/public/pay/${token}/paid`, { method }); toast.success('Obrigada! Vamos confirmar o pagamento.'); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Não foi possível registar'); } finally { setBusy(false); }
  };

  if (error) return <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center p-6 text-sm text-[#7A8278]" data-testid="pay-error">{error}</div>;
  if (!data) return <div className="min-h-screen bg-[#F7F5F0] flex items-center justify-center text-sm text-[#7A8278]" data-testid="pay-loading">A carregar...</div>;

  return (
    <div className="min-h-screen bg-[#F7F5F0] px-4 py-8" data-testid="pay-page">
      <div className="max-w-md mx-auto space-y-4">
        <p className="text-[11px] tracking-[0.14em] uppercase text-[#7A8276]">nutrianapereira</p>
        <h1 className="font-heading text-2xl text-[#2E3A33]" data-testid="pay-greeting">Olá, {data.first_name || 'bem-vinda'}</h1>
        <div className="bg-white border border-[#E8E3DB] rounded-2xl p-4 text-sm text-[#2E3A33] space-y-1" data-testid="pay-summary">
          <p>{data.service} com <strong>{withErs(data.nutritionist)}</strong></p>
          {data.kind === 'installment'
            ? <p data-testid="pay-installment-label"><strong>{data.installment_label}</strong> · vencimento {fmtDate(data.due_date)}</p>
            : <p>{fmtDate(data.date)} às {fmtTime(data.time)} · {data.modality}</p>}
          {data.amount ? <p>Valor: <strong>{fmtEur(data.amount)}</strong></p> : null}
        </div>

        {!data.active && <p className="text-sm text-[#7A8278]" data-testid="pay-inactive">{data.kind === 'installment' ? 'Esta prestação já não está ativa.' : 'Esta consulta já não está ativa.'}</p>}
        {data.is_paid && (
          <div className="bg-white border border-[#E8E3DB] rounded-2xl p-4 flex items-center gap-2 text-[#657161]" data-testid="pay-status-paid"><CheckCircle size={22} weight="duotone" /> Pagamento confirmado. Obrigada!</div>
        )}
        {data.pending_confirmation && !data.is_paid && (
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 flex items-start gap-2 text-amber-800 text-sm" data-testid="pay-status-pending">
            <Clock size={22} weight="duotone" className="flex-shrink-0" />
            <span>Recebemos o teu aviso de pagamento{data.declared_method ? ` (${data.declared_method})` : ''}. Assim que o confirmarmos, {data.kind === 'installment' ? 'a prestação fica como paga' : 'a consulta fica como paga'}.</span>
          </div>
        )}
        {data.active && !data.is_paid && !data.pending_confirmation && (
          data.methods.length === 0
            ? <p className="text-sm text-[#7A8278]" data-testid="pay-no-methods">De momento não há formas de pagamento disponíveis. Fala connosco.</p>
            : data.methods.map(m => <MethodCard key={m.key} m={m} busy={busy} onPaid={paid} />)
        )}
      </div>
    </div>
  );
}
